import { useState, useRef, useEffect, useMemo } from 'react';
import { Search, Download } from 'lucide-react';
import { usePodLogs } from './useClusterData';

interface Props {
  siteId: string;
  namespace: string;
  podName: string;
  containers: string[];
  onClose: () => void;
}

// Tail-lines selector: matches the operator-tier selector spec'd in #244
// (200 default, 500/1000 escalations). The 5000 option carried over from
// the engineer-era viewer is dropped — it's both rarely useful (kube
// throttles before that) and inconsistent with the support-bundle's
// 500-line cap.
const TAIL_OPTIONS = [200, 500, 1000] as const;
const TIME_OPTIONS = [
  { label: '5m', seconds: 300 },
  { label: '15m', seconds: 900 },
  { label: '1h', seconds: 3600 },
  { label: '6h', seconds: 21600 },
  { label: 'All', seconds: undefined },
] as const;

export default function PodLogsViewer({ siteId, namespace, podName, containers, onClose }: Props) {
  const [container, setContainer] = useState(containers[0] ?? '');
  // Default 200 — matches the site-operator default in #244. Engineers
  // can bump to 500/1000 from the same selector; both tiers see the
  // same control.
  const [tailLines, setTailLines] = useState<number>(200);
  const [timeRange, setTimeRange] = useState<number | undefined>(undefined);
  const [previous, setPrevious] = useState(false);
  const [searchText, setSearchText] = useState('');
  const logRef = useRef<HTMLPreElement>(null);

  const { data: logs, isLoading } = usePodLogs(siteId, namespace, podName, {
    container: container || undefined,
    tailLines,
    sinceSeconds: timeRange,
    previous,
  });

  // Auto-scroll to bottom on new logs
  useEffect(() => {
    if (logRef.current) {
      logRef.current.scrollTop = logRef.current.scrollHeight;
    }
  }, [logs]);

  const filteredLines = useMemo(() => {
    if (!logs) return [];
    const lines = logs.split('\n');
    if (!searchText) return lines;
    const q = searchText.toLowerCase();
    return lines.filter((l) => l.toLowerCase().includes(q));
  }, [logs, searchText]);

  function handleDownload() {
    if (!logs) return;
    const blob = new Blob([logs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${podName}-${container || 'all'}.log`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-[90vw] max-w-5xl h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-surface-elevated">
          <div className="flex items-center gap-3">
            <h3 className="text-sm font-medium text-silver-bright">
              Logs: <span className="text-gold font-mono">{podName}</span>
            </h3>
            {containers.length > 1 && (
              <select
                value={container}
                onChange={(e) => setContainer(e.target.value)}
                className="bg-surface-deep border border-surface-elevated rounded px-2 py-1 text-xs text-silver-bright focus:border-gold focus:outline-none"
              >
                {containers.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            )}
          </div>
          <button onClick={onClose} className="text-silver-muted hover:text-silver-bright text-lg px-2">&times;</button>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3 px-4 py-2 border-b border-surface-elevated flex-wrap">
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-silver-muted">Lines:</span>
            {TAIL_OPTIONS.map((n) => (
              <button
                key={n}
                data-testid={`pod-logs-tail-${n}`}
                onClick={() => setTailLines(n)}
                className={`px-2 py-0.5 text-xs rounded ${tailLines === n ? 'bg-gold/20 text-gold' : 'text-silver-muted hover:text-silver-bright'}`}
              >
                {n}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-surface-elevated" />

          <div className="flex items-center gap-1.5">
            <span className="text-xs text-silver-muted">Time:</span>
            {TIME_OPTIONS.map((t) => (
              <button
                key={t.label}
                onClick={() => setTimeRange(t.seconds)}
                className={`px-2 py-0.5 text-xs rounded ${timeRange === t.seconds ? 'bg-gold/20 text-gold' : 'text-silver-muted hover:text-silver-bright'}`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="w-px h-4 bg-surface-elevated" />

          <label className="flex items-center gap-1.5 text-xs text-silver-muted cursor-pointer">
            <input
              type="checkbox"
              checked={previous}
              onChange={(e) => setPrevious(e.target.checked)}
              className="rounded border-surface-elevated"
            />
            Previous
          </label>

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search size={10} className="absolute left-2 top-1/2 -translate-y-1/2 text-silver-muted" />
              <input
                type="text"
                placeholder="Filter..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="bg-surface-deep border border-surface-elevated rounded pl-6 pr-2 py-1 text-xs text-silver-bright placeholder:text-silver-muted/50 focus:border-gold focus:outline-none w-36"
              />
            </div>
            <button onClick={handleDownload} className="text-silver-muted hover:text-silver-bright" title="Download logs">
              <Download size={14} />
            </button>
          </div>
        </div>

        {/* Log output */}
        <pre
          ref={logRef}
          className="flex-1 overflow-auto px-4 py-3 bg-[#0d1117] text-[#c9d1d9] font-mono text-xs leading-relaxed whitespace-pre-wrap"
        >
          {isLoading ? (
            <span className="text-silver-muted animate-pulse">Loading logs...</span>
          ) : filteredLines.length === 0 ? (
            <span className="text-silver-muted">No logs available</span>
          ) : (
            filteredLines.map((line, i) => (
              <div key={i} className="hover:bg-white/5">
                {searchText && line.toLowerCase().includes(searchText.toLowerCase()) ? (
                  highlightSearch(line, searchText)
                ) : (
                  line
                )}
              </div>
            ))
          )}
        </pre>
      </div>
    </div>
  );
}

function highlightSearch(line: string, search: string): React.ReactNode {
  const idx = line.toLowerCase().indexOf(search.toLowerCase());
  if (idx === -1) return line;
  return (
    <>
      {line.slice(0, idx)}
      <mark className="bg-gold/30 text-gold">{line.slice(idx, idx + search.length)}</mark>
      {line.slice(idx + search.length)}
    </>
  );
}
